import { useState, useEffect } from 'react';
import type { User } from 'firebase/auth';
import { isPreview, PREVIEW_USER } from '@/lib/preview';

// Evaluated once at module load; stable for the tab, so the early return below
// never changes hook order across renders.
const PREVIEW = isPreview();

// Belt-and-suspenders ceiling on how long we'll wait for Firebase to deliver
// an auth-state event before giving up and treating the tab as
// unauthenticated. Exists because a Firebase init-time failure (bad/missing
// API key, blocked script, offline, etc.) doesn't reliably surface as a
// promise rejection we can `.catch` — the SDK can fail internally (e.g. a
// background token-refresh call rejecting with `auth/invalid-api-key`,
// confirmed via console in PLAN.md #98's grandma audit) without ever
// invoking `onAuthStateChanged`'s callback, which used to leave `isLoading`
// — and every page gated on it (portal, EmailVerificationGuard,
// subscribe/paywall) — spinning forever with no escape route
// (`/portal/boost`'s permanent blank spinner).
const AUTH_RESOLUTION_TIMEOUT_MS = 8000;

export function useAuth() {
  if (PREVIEW) {
    return { user: PREVIEW_USER, isLoading: false, isAuthenticated: true };
  }

  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Firebase (~100+ KiB) is loaded on-demand here rather than as a static
    // import — this hook runs on every page via App.tsx's Router, so a
    // static import would put the full SDK in the main bundle for pages
    // that never touch auth. `cancelled` guards against the classic
    // effect-cleanup-before-async-resolves race (fast unmount, e.g. rapid
    // route changes) so we never subscribe after the component is gone.
    let unsubscribe: (() => void) | undefined;
    let cancelled = false;
    let authTimeoutId: ReturnType<typeof setTimeout> | undefined;

    // Called the moment we have a real answer (an auth-state event fired, or
    // the import itself failed) so the timeout below never fires a
    // false-positive "unauthenticated" after a slow-but-successful
    // resolution — `clearTimeout` guarantees the pending callback won't run.
    function markResolved() {
      if (authTimeoutId !== undefined) {
        clearTimeout(authTimeoutId);
        authTimeoutId = undefined;
      }
    }

    function startAuthListener() {
      import('@/lib/firebase')
        .then(({ onAuthStateChange }) => {
          if (cancelled) return;
          unsubscribe = onAuthStateChange(async (firebaseUser) => {
            markResolved();
            setUser(firebaseUser);
            setIsLoading(false);

            if (firebaseUser) {
              try {
                const token = await firebaseUser.getIdToken();
                window.postMessage({
                  type: 'TRAININGBOOKER_AUTH_SUCCESS',
                  token,
                  userId: firebaseUser.uid
                }, '*');
              } catch (error) {
                console.error('Failed to get auth token:', error);
              }
            }

            // REMOVED: Redirect logic moved to SignInForm for immediate redirect after auth
            // This eliminates the blank page flash and speeds up the login experience
          });
        })
        .catch((error) => {
          // The dynamic import itself failed (network/module-load error) —
          // no listener ever got subscribed, so nothing will ever call
          // setIsLoading(false) on its own. Resolve to "unauthenticated"
          // rather than hang; log so the failure is still visible.
          if (cancelled) return;
          console.error('Failed to load Firebase auth module:', error);
          markResolved();
          setIsLoading(false);
        });
    }

    // "Going for 100" PageSpeed follow-up (found 2026-08-22, after tasks.md
    // #42): the Firebase import above used to fire synchronously the instant
    // this effect ran — on every single page, including pure-public ones
    // like Home that never render anything auth-dependent (checked:
    // SimpleHeader doesn't call useAuth() at all). Nudge it to idle time
    // instead of the render-critical tick. Deliberately NOT the
    // window.load+multi-second-timer treatment used for Sentry/GTM: those
    // have zero UI tied to their timing, but real pages here (portal,
    // EmailVerificationGuard, subscribe/paywall) genuinely need auth to
    // resolve promptly — they're just not what PageSpeed's public-page
    // audit scores. `requestIdleCallback` alone (short timeout as a safety
    // net for browsers without it, e.g. Safari) gets it off the critical
    // tick without meaningfully delaying real auth resolution.
    let idleHandle: number | undefined;
    let timeoutHandle: ReturnType<typeof setTimeout> | undefined;
    if ('requestIdleCallback' in window) {
      idleHandle = window.requestIdleCallback(startAuthListener, { timeout: 1500 });
    } else {
      timeoutHandle = setTimeout(startAuthListener, 0);
    }

    // Dead-man's switch: covers the idle-callback delay above (up to 1.5s)
    // plus real Firebase init/network time, then gives up if NEITHER the
    // listener callback NOR the import's own `.catch` above has fired —
    // i.e. exactly the internal-SDK-hang case those two can't catch.
    // `markResolved()` (called from both success and failure paths above)
    // clears this before it can ever fire on a real, even if slow, resolution.
    authTimeoutId = setTimeout(() => {
      if (cancelled) return;
      console.error(
        `Firebase auth did not resolve within ${AUTH_RESOLUTION_TIMEOUT_MS}ms — treating as unauthenticated.`
      );
      setIsLoading(false);
    }, AUTH_RESOLUTION_TIMEOUT_MS);

    return () => {
      cancelled = true;
      unsubscribe?.();
      if (idleHandle !== undefined) window.cancelIdleCallback?.(idleHandle);
      if (timeoutHandle !== undefined) clearTimeout(timeoutHandle);
      if (authTimeoutId !== undefined) clearTimeout(authTimeoutId);
    };
  }, []);

  return {
    user,
    isLoading,
    isAuthenticated: !!user,
  };
}